import { Autocomplete, InputAdornment, MenuItem, Stack, TextField } from '@mui/material';
import { FC, useMemo } from 'react';
import { FixedTimeValue, FixedValue } from 'src/entities/import';
import { PlaceEntity } from 'src/entities/placeEntity';
import { ShipperEntity } from 'src/entities/shipperEntity';
import { VehicleAttributeCategory } from 'src/entities/VehicleAttributeValue.entity';
import { useImportFixedValueMasters } from 'src/hooks/useImportFixedValueMasters';
import { useVehicleAttributeOptions } from 'src/hooks/useVehicleAttributeOptions';

import { FIXED_SELECT_OPTIONS, FIXED_UNITS, FixedFieldType, relativeDayLabel } from './fixedValues';
import PlaceNameAutocomplete from './PlaceNameAutocomplete';
import ShipperAutocomplete from './ShipperAutocomplete';

export type FixedValueInputProps = {
  fieldKey: string;
  label: string;
  type: FixedFieldType;
  value: FixedValue | undefined;
  onChange: (value: FixedValue) => void;
  onShipperSelect?: (shipper: ShipperEntity) => void;
  onPlaceSelect?: (place: PlaceEntity | null) => void;
  companyId: number;
  /** 荷主名の候補。呼び出し側が名前で重複を除いて渡す */
  shippers?: ShipperEntity[];
  /** 積地名称・降地名称の候補。呼び出し側が location_role で絞って渡す */
  places?: PlaceEntity[];
  /** 荷主・地点の候補を読み込み中 */
  loading?: boolean;
};

const INPUT_LABEL = '固定値（全行に適用）';

const VEHICLE_ATTRIBUTE_CATEGORY_OF: Record<string, VehicleAttributeCategory> = {
  designated_truck_klasses: 'klass',
  designated_truck_car_models: 'car_model',
  designated_truck_loading_platform_heights: 'loading_platform_height',
  designated_truck_loading_platform_widths: 'loading_platform_width',
  designated_truck_loading_platform_lengths: 'loading_platform_length',
  designated_truck_floor_specifications: 'floor_specification',
  designated_truck_features: 'features',
};

const BASE_RELATIVE_DAYS = [-1, 0, 1];

const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;

// Select は aria-labelledby（見えるラベル＋選択中の値）が aria-label より優先されるので、
// 項目名を付けた名前で読み上げ・テストで区別できるよう aria-labelledby を外す
const selectDisplayNamed = (name: string) => ({
  SelectDisplayProps: { 'aria-label': name, 'aria-labelledby': undefined },
});

const asString = (value: FixedValue | undefined): string => (typeof value === 'string' ? value : '');

const asStrings = (value: FixedValue | undefined): string[] => (
  Array.isArray(value) ? value.filter((it): it is string => typeof it === 'string') : []
);

const asIds = (value: FixedValue | undefined): number[] => (
  Array.isArray(value) ? value.filter((it): it is number => typeof it === 'number') : []
);

const asTime = (value: FixedValue | undefined): FixedTimeValue => (
  typeof value === 'object' && !Array.isArray(value) ? value : { time: '', relative_day: 0 }
);

// API は数値を /^-?\d+(\.\d+)?$/ の文字列で受ける。type="number" の入力は '1e3' のような
// 指数表記も値として返すので、数字だけの文字列に直す。
const toPlainNumber = (text: string): string => {
  if (text === '' || PLAIN_NUMBER.test(text)) return text;
  const number = Number(text);
  if (!Number.isFinite(number)) return '';
  return number.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 });
};

type InputProps = Omit<FixedValueInputProps, 'type'> & { accessibleName: string };

const MultiInput: FC<InputProps> = ({ fieldKey, value, onChange, companyId, accessibleName }) => {
  const { optionsOf, isLoading } = useVehicleAttributeOptions(companyId);
  const current = asStrings(value);
  const options = optionsOf(VEHICLE_ATTRIBUTE_CATEGORY_OF[fieldKey], current);

  return (
    <TextField
      select
      size="small"
      fullWidth
      label={INPUT_LABEL}
      value={current}
      disabled={isLoading}
      onChange={(event) => {
        const selected = event.target.value as unknown as string | string[];
        onChange(typeof selected === 'string' ? selected.split(',') : selected);
      }}
      slotProps={{
        inputLabel: { shrink: true },
        select: {
          multiple: true,
          renderValue: (selected) => (selected as string[]).join('、'),
          ...selectDisplayNamed(accessibleName),
        },
      }}
    >
      {options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
    </TextField>
  );
};

type MasterOption = { id: number; label: string };

const MasterIdsInput: FC<InputProps & { kind: 'trucks' | 'drivers' }> = ({
  kind, value, onChange, companyId, accessibleName,
}) => {
  const masters = useImportFixedValueMasters(companyId);
  const options = masters[kind];
  const unknownLabel = kind === 'trucks' ? '不明なトラック' : '不明なドライバー';
  // 一覧に無い id（無効にした・一覧の取得前）を勝手に消さないため、名前を付けて残す。
  // 描画のたびに新しい配列を渡すと、Autocomplete が値が変わったとみなして入力中の検索文字を消す
  const selected = useMemo(() => asIds(value).map((id): MasterOption => (
    options.find((option) => option.id === id) ?? { id, label: `${unknownLabel}（id: ${id}）` }
  )), [value, options, unknownLabel]);

  return (
    <Autocomplete<MasterOption, true>
      multiple
      size="small"
      fullWidth
      options={options}
      value={selected}
      loading={masters.isLoading}
      isOptionEqualToValue={(option, item) => option.id === item.id}
      getOptionLabel={(option) => option.label}
      onChange={(_, items) => onChange(items.map((item) => item.id))}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return <li key={key} {...optionProps}>{option.label}</li>;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={INPUT_LABEL}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          inputProps={{ ...params.inputProps, 'aria-label': accessibleName }}
        />
      )}
    />
  );
};

const TimeInput: FC<InputProps> = ({ label, value, onChange, accessibleName }) => {
  const current = asTime(value);
  const days = BASE_RELATIVE_DAYS.includes(current.relative_day)
    ? BASE_RELATIVE_DAYS
    : [...BASE_RELATIVE_DAYS, current.relative_day].sort((a, b) => a - b);

  return (
    <Stack direction="row" spacing={1}>
      <TextField
        select
        size="small"
        label="日"
        value={current.relative_day}
        onChange={(event) => onChange({ ...current, relative_day: Number(event.target.value) })}
        sx={{ minWidth: 96 }}
        slotProps={{ select: selectDisplayNamed(`${label}の日`) }}
      >
        {days.map((day) => <MenuItem key={day} value={day}>{relativeDayLabel(day)}</MenuItem>)}
      </TextField>
      <TextField
        type="time"
        size="small"
        fullWidth
        label={INPUT_LABEL}
        value={current.time}
        onChange={(event) => onChange({ ...current, time: event.target.value })}
        slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'aria-label': accessibleName } }}
      />
    </Stack>
  );
};

const FixedValueInput: FC<FixedValueInputProps> = ({ type, ...props }) => {
  const {
    fieldKey, label, value, onChange, onShipperSelect, onPlaceSelect, shippers = [], places = [], loading = false,
  } = props;
  const id = `fixed-value-${fieldKey}`;
  // 見えるラベルは全項目で同じなので、どの項目の入力欄かを読み上げ・テストで区別できるようにする
  const accessibleName = `${label}の${INPUT_LABEL}`;
  const inputProps = { ...props, accessibleName };
  const text = asString(value);

  switch (type) {
    case 'time':
      return <TimeInput {...inputProps} />;
    case 'multi':
      return <MultiInput {...inputProps} />;
    case 'trucks':
    case 'drivers':
      return <MasterIdsInput {...inputProps} kind={type} />;
    case 'shipper':
      return (
        <ShipperAutocomplete
          id={id}
          label={INPUT_LABEL}
          shipperData={shippers}
          selectedShipperId={shippers.find((shipper) => shipper.name === text)?.id ?? null}
          selectedShipperName={text || null}
          loading={loading}
          // 荷主 id は持たない。選んだ荷主は onShipperSelect で受け、候補の削除で外れたときは名前の変更で受ける
          onShipperIdChange={() => {}}
          onShipperNameChange={(name) => onChange(name ?? '')}
          onShipperSelect={(shipper) => (onShipperSelect ? onShipperSelect(shipper) : onChange(shipper.name))}
        />
      );
    case 'place':
      return (
        <PlaceNameAutocomplete
          id={id}
          label={INPUT_LABEL}
          places={places}
          value={text}
          loading={loading}
          onNameChange={(name) => onChange(name)}
          onPlaceSelect={(place) => onPlaceSelect?.(place)}
        />
      );
    case 'select':
      return (
        <TextField
          select
          size="small"
          fullWidth
          label={INPUT_LABEL}
          value={text}
          onChange={(event) => onChange(event.target.value)}
          slotProps={{
            inputLabel: { shrink: true },
            select: { displayEmpty: true, ...selectDisplayNamed(accessibleName) },
          }}
        >
          <MenuItem value="">未選択</MenuItem>
          {(FIXED_SELECT_OPTIONS[fieldKey] ?? []).map((option) => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </TextField>
      );
    default: {
      const unit = FIXED_UNITS[fieldKey];
      return (
        <TextField
          type={type === 'textarea' ? 'text' : type}
          multiline={type === 'textarea'}
          size="small"
          fullWidth
          label={INPUT_LABEL}
          value={type === 'date' ? text.replaceAll('/', '-') : text}
          onChange={(event) => {
            const input = event.target.value;
            if (type === 'date') onChange(input.replaceAll('-', '/'));
            else if (type === 'number') onChange(toPlainNumber(input));
            else onChange(input);
          }}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { 'aria-label': accessibleName },
            input: unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined,
          }}
        />
      );
    }
  }
};

export default FixedValueInput;
